export interface GroqMessage {
    role: 'system' | 'user' | 'assistant';
    content: string;
}

const GROQ_API_KEY = process.env.REACT_APP_GROQ_API_KEY || '';
const MODEL = 'llama-3.3-70b-versatile';
const BASE_URL = 'https://api.groq.com/openai/v1/chat/completions';

export const AL_STREAMS: Record<string, string[]> = {
    'Mathematics': ['Combined Maths', 'Pure Maths', 'Applied Maths', 'Physics'],
    'Bio Science': ['Biology', 'Chemistry', 'Physics'],
    'Commerce': ['Accounting', 'Business Studies', 'Economics'],
    'Arts': ['Sinhala', 'Geography', 'History', 'Political Science', 'Buddhist Civilization', 'English Literature'],
    'Technology': ['Engineering Technology', 'Science for Technology', 'ICT'],
};

export function buildSystemPrompt(stream: string, subject: string, pdfContext?: string): string {
    let prompt = `You are an expert Sri Lankan Advanced Level (A/L) tutor specializing in ${subject} under the ${stream} stream. You are deeply familiar with the Sri Lankan A/L curriculum prescribed by the Department of Examinations, Sri Lanka.

Guidelines:
- Explain concepts clearly with step-by-step reasoning
- When solving math, physics, or chemistry problems, show all working steps
- Reference the Sri Lankan A/L syllabus topics when relevant
- Provide examples that are relevant to Sri Lankan students
- Stay focused on ${subject} topics. If asked about unrelated subjects, politely redirect the student back to ${subject}
- Use Markdown formatting for equations, lists, and code blocks
- Use LaTeX notation ($...$ for inline, $$...$$ for block) for mathematical expressions
- Be encouraging and supportive in your teaching style
- When appropriate, mention tips for A/L exam preparation
- If the student asks in Sinhala or Tamil, try to respond in the same language while keeping technical terms in English`;

    if (pdfContext) {
        prompt += `\n\n--- REFERENCE MATERIAL ---
The student has uploaded study material. Use the following content to answer their questions accurately. When referencing this material, indicate that you're drawing from their uploaded document.

${pdfContext}
--- END REFERENCE MATERIAL ---`;
    }

    return prompt;
}

function formatHistory(messages: { role: 'user' | 'model'; text: string }[]): GroqMessage[] {
    return messages.map(msg => ({
        role: msg.role === 'model' ? 'assistant' as const : 'user' as const,
        content: msg.text,
    }));
}

function getErrorMessage(status: number): string {
    switch (status) {
        case 400:
            return 'Sorry, I couldn\'t process that request. Please try rephrasing your question.';
        case 401:
            return 'API key is invalid. Please check your Groq API key configuration.';
        case 403:
            return 'API key configuration error. Please contact support.';
        case 429:
            return 'Too many requests. Please wait a moment and try again.';
        default:
            if (status >= 500) return 'The AI service is temporarily unavailable. Please try again later.';
            return 'An unexpected error occurred. Please try again.';
    }
}

export async function sendMessageStream(
    history: { role: 'user' | 'model'; text: string }[],
    userMessage: string,
    systemPrompt: string,
    onChunk: (accumulatedText: string) => void
): Promise<string> {
    if (!GROQ_API_KEY) {
        throw new Error('Groq API key is not configured. Please add REACT_APP_GROQ_API_KEY to your .env file.');
    }

    const recentHistory = history.slice(-40);
    const messages: GroqMessage[] = [
        { role: 'system', content: systemPrompt },
        ...formatHistory(recentHistory),
        { role: 'user', content: userMessage },
    ];

    let response: Response;
    try {
        response = await fetch(BASE_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${GROQ_API_KEY}`,
            },
            body: JSON.stringify({
                model: MODEL,
                messages,
                stream: true,
                temperature: 0.5,
                max_tokens: 4096,
            }),
        });
    } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Network error';
        console.error('Network error:', errorMessage);
        throw new Error(`Connection failed: ${errorMessage}. Please check your internet connection and try again.`);
    }

    if (!response.ok) {
        throw new Error(getErrorMessage(response.status));
    }

    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let fullText = '';
    let buffer = '';

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed.startsWith('data: ')) continue;
            const jsonStr = trimmed.slice(6);
            if (jsonStr === '[DONE]') continue;

            try {
                const parsed = JSON.parse(jsonStr);
                const content = parsed?.choices?.[0]?.delta?.content;
                if (content) {
                    fullText += content;
                    onChunk(fullText);
                }
            } catch {
                // Skip malformed JSON chunks
            }
        }
    }

    return fullText;
}

export async function sendMessage(
    history: { role: 'user' | 'model'; text: string }[],
    userMessage: string,
    systemPrompt: string
): Promise<string> {
    if (!GROQ_API_KEY) {
        throw new Error('Groq API key is not configured. Please add REACT_APP_GROQ_API_KEY to your .env file.');
    }

    const recentHistory = history.slice(-40);
    const messages: GroqMessage[] = [
        { role: 'system', content: systemPrompt },
        ...formatHistory(recentHistory),
        { role: 'user', content: userMessage },
    ];

    let response: Response;
    try {
        response = await fetch(BASE_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${GROQ_API_KEY}`,
            },
            body: JSON.stringify({
                model: MODEL,
                messages,
                temperature: 0.5,
                max_tokens: 4096,
            }),
        });
    } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Network error';
        console.error('Network error:', errorMessage);
        throw new Error(`Connection failed: ${errorMessage}. Please check your internet connection and try again.`);
    }

    if (!response.ok) {
        throw new Error(getErrorMessage(response.status));
    }

    const data = await response.json();
    const text = data?.choices?.[0]?.message?.content;
    if (!text) {
        throw new Error('No response received from the AI. Please try again.');
    }

    return text;
}
